import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, Copy, RotateCcw } from 'lucide-react';

const BusinessHoursEditor = ({ value, onChange, t, className = '' }) => {
  const [hours, setHours] = useState({
    Monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
    Saturday: { isOpen: true, openTime: '10:00', closeTime: '16:00' },
    Sunday: { isOpen: false, openTime: '10:00', closeTime: '16:00' }
  });

  const days = useMemo(() => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], []);

  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayTime = formatTime12Hour(timeString);
      timeOptions.push({ value: timeString, label: displayTime });
    }
  }

  function formatTime12Hour(time24) {
    const [hours, minutes] = time24.split(':');
    let hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${ampm}`;
  }

  const generateTextOutput = useCallback(() => {
    const result = [];
    let currentGroup = { days: [], hours: null };

    days.forEach((day, index) => {
      const dayInfo = hours[day];
      const hoursText = dayInfo.isOpen 
        ? `${formatTime12Hour(dayInfo.openTime)} - ${formatTime12Hour(dayInfo.closeTime)}`
        : 'Closed';

      if (currentGroup.hours === hoursText) {
        currentGroup.days.push(day);
      } else {
        if (currentGroup.days.length > 0) {
          const dayRange = currentGroup.days.length === 1 
            ? currentGroup.days[0]
            : `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}`;
          result.push(`${dayRange}: ${currentGroup.hours}`);
        }
        currentGroup = { days: [day], hours: hoursText };
      }

      if (index === days.length - 1) {
        const dayRange = currentGroup.days.length === 1 
          ? currentGroup.days[0]
          : `${currentGroup.days[0]} - ${currentGroup.days[currentGroup.days.length - 1]}`;
        result.push(`${dayRange}: ${currentGroup.hours}`);
      }
    });

    return result.join('\n');
  }, [hours, days]);

  const parseExistingValue = useCallback((text) => {
    const lines = text.split('\n').filter(line => line.trim());
    
    setHours(currentHours => {
      const newHours = { ...currentHours };

      lines.forEach(line => {
        const match = line.match(/^(.+?):\s*(.+)$/);
        if (match) {
          const dayPart = match[1].trim();
          const timePart = match[2].trim();

          let targetDays = [];
          if (dayPart.includes('-')) {
            const [startDay, endDay] = dayPart.split('-').map(d => d.trim());
            const startIndex = days.indexOf(startDay);
            const endIndex = days.indexOf(endDay);
            if (startIndex >= 0 && endIndex >= 0) {
              for (let i = startIndex; i <= endIndex; i++) {
                targetDays.push(days[i]);
              }
            }
          } else {
            if (days.includes(dayPart)) {
              targetDays.push(dayPart);
            }
          }

          targetDays.forEach(day => {
            if (timePart.toLowerCase().includes('closed')) {
              newHours[day] = { ...newHours[day], isOpen: false };
            } else {
              const timeMatch = timePart.match(/(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);
              if (timeMatch) {
                const openTime = convertTo24Hour(timeMatch[1], timeMatch[2], timeMatch[3]);
                const closeTime = convertTo24Hour(timeMatch[4], timeMatch[5], timeMatch[6]);
                newHours[day] = { isOpen: true, openTime, closeTime };
              }
            }
          });
        }
      });

      return newHours;
    });
  }, [days]);

  useEffect(() => {
    if (value && value.trim()) {
      parseExistingValue(value);
    }
  }, [value, parseExistingValue]);

  useEffect(() => {
    const output = generateTextOutput();
    onChange(output);
  }, [hours, onChange, generateTextOutput]);

  function convertTo24Hour(hour, minute, ampm) {
    let h = parseInt(hour);
    if (ampm.toUpperCase() === 'PM' && h !== 12) h += 12;
    if (ampm.toUpperCase() === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${minute}`;
  }

  function updateDay(day, field, value) {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  }

  function copyToAllDays(sourceDay) {
    const sourceHours = hours[sourceDay];
    setHours(prev => {
      const updated = { ...prev };
      days.forEach(day => {
        if (day !== sourceDay) {
          updated[day] = { ...sourceHours };
        }
      });
      return updated;
    });
  }

  function resetToDefault() {
    setHours({
      Monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Saturday: { isOpen: true, openTime: '10:00', closeTime: '16:00' },
      Sunday: { isOpen: false, openTime: '10:00', closeTime: '16:00' }
    });
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#a15507]" />
          <h3 className="text-lg font-semibold text-white">Business Hours</h3>
        </div>
        <button
          type="button"
          onClick={resetToDefault}
          className="flex items-center gap-2 px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      <div className="space-y-3">
        {days.map(day => (
          <div key={day} className="bg-[#374151] rounded-lg p-4 border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-medium text-white min-w-[100px]">{day}</span>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={hours[day].isOpen}
                    onChange={(e) => updateDay(day, 'isOpen', e.target.checked)}
                    className="rounded border-gray-500 text-[#a15507] focus:ring-[#a15507] focus:ring-offset-0"
                  />
                  <span className="text-sm text-gray-300">Open</span>
                </label>
              </div>
              <button
                type="button"
                onClick={() => copyToAllDays(day)}
                className="flex items-center gap-1 text-xs text-[#a15507] hover:text-[#924e06] transition-colors"
              >
                <Copy className="w-3 h-3" />
                Copy to All
              </button>
            </div>
            
            {hours[day].isOpen && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Opening Time</label>
                  <select
                    value={hours[day].openTime}
                    onChange={(e) => updateDay(day, 'openTime', e.target.value)}
                    className="w-full bg-[#1f2937] border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507]"
                  >
                    {timeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Closing Time</label>
                  <select
                    value={hours[day].closeTime}
                    onChange={(e) => updateDay(day, 'closeTime', e.target.value)}
                    className="w-full bg-[#1f2937] border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507]"
                  >
                    {timeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600/50">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Preview:</h4>
        <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono">
          {generateTextOutput()}
        </pre>
      </div>
    </div>
  );
};

export default BusinessHoursEditor;
